import React, { useState, useEffect } from 'react';
import { Modal, Button } from '@availity/element';
import { useSessionStore } from '../../hooks';
import type { SessionStore } from '../../auth/sessionService';
import authConstants from '../../auth/authConstants';
import { colors } from '../../static/constants/theme';
import { typography } from '../../static/constants/typography';

const { ACTIVITY_SYNC_KEY, getNewWarningTimer, getNewSessionTimer } = authConstants;

const getDiffTimeInSeconds = (sessionExpiry: number) => Math.max(0, Math.floor((sessionExpiry - Date.now()) / 1000));

const handleStayLoggedIn = (sessionStore: SessionStore) => {
  const currentTime = Date.now();
  const now = currentTime.toString();
  localStorage.setItem(ACTIVITY_SYNC_KEY, now);
  sessionStore.setLastActivityTime(now);
  sessionStore.setWarningTime(getNewWarningTimer(currentTime));
  sessionStore.setSessionExpiry(getNewSessionTimer(currentTime));
  sessionStore.setShowWarning(false);
};

const SessionWarningModal: React.FC = () => {
  const { sessionStore, isAuthenticated, showWarning, sessionExpiry } = useSessionStore((store: unknown) => {
    const sessionStore = store as SessionStore;
    return {
      sessionStore,
      isAuthenticated: sessionStore.isAuthenticated,
      showWarning: sessionStore.showWarning,
      sessionExpiry: sessionStore.sessionExpiry,
    };
  });

  const [timeRemainingInSecs, setTimeRemainingInSecs] = useState(() => getDiffTimeInSeconds(sessionExpiry));

  useEffect(() => {
    if (!showWarning || !isAuthenticated ) return;
    setTimeRemainingInSecs(getDiffTimeInSeconds(sessionExpiry));
    const interval = setInterval(() => setTimeRemainingInSecs(getDiffTimeInSeconds(sessionExpiry)), 1000);
    // eslint-disable-next-line consistent-return
    return () => clearInterval(interval);
  }, [showWarning, sessionExpiry, isAuthenticated]);

  const minutes = Math.ceil(timeRemainingInSecs / 60);
  const timeLabel = timeRemainingInSecs >= 60
    ? `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
    : `${timeRemainingInSecs} ${timeRemainingInSecs === 1 ? 'second' : 'seconds'}`;
  
  if (!isAuthenticated || !showWarning) return null;
  return (
    <Modal
      open={showWarning}
      disableEscapeKeyDown
      hideBackdrop={false}
      data-testid="session-warning-modal"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& .MuiBackdrop-root': { backgroundColor: colors.background.backdrop },
      }}
    >
      <div
        style={{
          padding: '24px',
          backgroundColor: colors.white,
          borderRadius: '8px',
          maxWidth: '500px',
          width: '90%',
          outline: 'none',
        }}
      >
        <h3
          style={{
            marginTop: 0,
            marginBottom: '16px',
            textAlign: 'center',
            color: colors.text.primary,
            ...typography.montSemibold20,
          }}
        >
          Session Timeout Warning
        </h3>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <p style={{ margin: 0, color: colors.text.dark, ...typography.latoRegular16 }}>
            Your session will expire in {timeLabel} due to inactivity.
          </p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
          <Button onClick={() => handleStayLoggedIn(sessionStore)}>Stay Logged In</Button>
        </div>
      </div>
    </Modal>
  );
};

export default SessionWarningModal;
